import React, { useEffect, useState } from 'react';

import { Button, Radio } from 'semantic-ui-react';

import { SatCoreComponent, SatCoreRegister } from '../../SatCoreRegistry';
import Modal from '../Modal';

/**
 * Single-select language picker used by the Accommodations table for both the
 * Spot Translation (ReadSpeaker/TextHelp target) and Content Translation (C2C)
 * columns. Nothing is saved until Confirm is pressed.
 *
 * @param {{
 *   clearOptionLabel?: string,          // when set, a leading option that confirms `null` (no preference)
 *   close: () => void,
 *   description: string,
 *   languageNames: Record<string,string>, // { localeId: displayName }
 *   onConfirm: (localeId: string|null) => Promise<void>|void,
 *   open: boolean,
 *   selectedLanguage?: string|null,
 *   title: string
 * }} props
 * `t` is injected by SatCoreRegister (namespace `TranslationLanguageModal`).
 */
const TranslationLanguageModal = (props) => {
  const {
    clearOptionLabel = null,
    close,
    description,
    languageNames = {},
    onConfirm,
    open,
    selectedLanguage = null,
    t,
    title
  } = props;
  const ModalBanner = SatCoreComponent('ModalBanner');

  const [pendingLanguage, setPendingLanguage] = useState(selectedLanguage);
  const [saving, setSaving] = useState(false);

  // Reset the pending choice whenever the modal is (re)opened for a different value
  useEffect(() => {
    if (open) {
      setPendingLanguage(selectedLanguage);
      setSaving(false);
    }
  }, [open, selectedLanguage]);

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await onConfirm(pendingLanguage);
    } finally {
      setSaving(false);
      close();
    }
  };

  const languages = Object.keys(languageNames);

  return (
    <Modal
      className='translation-language-modal'
      closeOnDimmerClick={false}
      onClose={close}
      open={open}
      size='tiny'>
      <ModalBanner label={title} onClose={close} />
      <Modal.Content scrolling>
        <p className='translation-language-modal-description'>{description}</p>
        <div className='translation-language-selector'>
          <div className='selector-header'>
            <span className='selector-header-label'>{t('selectLanguage')}</span>
          </div>
          <div className='selector-body'>
            {clearOptionLabel && (
              <label key='__clear__' className='language-option'>
                <Radio
                  checked={!pendingLanguage}
                  onChange={() => setPendingLanguage(null)}
                />
                <span>{clearOptionLabel}</span>
              </label>
            )}
            {languages.map((lang) => (
              <label key={lang} className='language-option'>
                <Radio
                  checked={pendingLanguage === lang}
                  onChange={() => setPendingLanguage(lang)}
                />
                <span>{languageNames[lang] || lang}</span>
              </label>
            ))}
          </div>
        </div>
      </Modal.Content>
      <Modal.Actions>
        {!saving && <Button basic className='cancelButton' onClick={close} primary>{t('cancel')}</Button>}
        <Button
          className='saveButton'
          disabled={saving}
          loading={saving}
          onClick={handleConfirm}
          primary>
          {t('confirm')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default TranslationLanguageModal;

SatCoreRegister('TranslationLanguageModal', TranslationLanguageModal);
